import { MessageService } from 'primeng/api';
import { v4 as uuid } from 'uuid';

import { Injectable, OnDestroy } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Notification } from '../interfaces/notification.interface';
import { AuthStateService } from './authstate.service';
import { TranslocoService } from '@jsverse/transloco';
import { WebsocketService } from './websocket.service';

@Injectable({
    providedIn: 'root',
})
export class NotificationsService implements OnDestroy {

    //^ ////////////////////////////////////////////////////////////////////
    //^  INIZIALIZZAZIONI --------------------------------------------------
    //^ ////////////////////////////////////////////////////////////////////

    private notificationsHistory: Notification[] = []

    private selectedNotifications: Notification[] = []

    notificationsPanelVisible = false;

    private configDefaults: Notification = {
        id: '',
        severity: 'info',
        title: '--',
        detail: undefined,
        source: undefined,
        icon: undefined,
        life: 3000,
        closable: true,
        sticky: false,
        priority: 0,
        deletable: true,
        addToHistory: true,
        timestamp: undefined
    }

    //^ ////////////////////////////////////////////////////////////////////
    //^ COSTRUTTORE E LIFECYCLES -------------------------------------------
    //^ ////////////////////////////////////////////////////////////////////

    constructor(
        private messages: MessageService,
        private auth: AuthStateService,
        private transloco: TranslocoService,
        private wss: WebsocketService
    ) {
        wss.eventsSubject$.pipe(
            takeUntil(this.destroy$)
        ).subscribe({
            next: (e) => {
                if (e.type == "REPORT_NOTIFICATION") {
                    this.showNotification({
                        severity: 'warn',
                        title: 'newUserReportReceived',
                        icon: 'pi pi-flag',
                        life: 6000
                    })
                } else if (e.type == "REBALANCING_CALCULATED") {
                    this.showNotification({
                        severity: 'success',
                        title: 'rebalancingCalculated',
                        detail: "rebalancingCalculatedDetail",
                        icon: 'pi pi-check',
                        life: 10000
                    })
                } else if (e.type == "REBALANCING_STARTED") {
                    this.showNotification({
                        severity: 'info',
                        title: 'rebalancingStarted',
                        detail: "rebalancingStartedDetail",
                        icon: 'pi pi-info-circle',
                        life: 5000
                    })
                } else if (e.type == "PREDICTIONS_STARTED") {
                    this.showNotification({
                        severity: 'info',
                        title: 'predictionsStarted',
                        detail: "predictionsStartedDetail",
                        icon: 'pi pi-info-circle',
                        life: 5000
                    })
                } else if (e.type == "PREDICTIONS_FAILED") {
                    this.showNotification({
                        severity: 'error',
                        title: "errors.operationFailed",
                        detail: "errorCalculatingPredictions"
                    })
                } else if (e.type == "PREDICTIONS_CALCULATED") {
                    this.showNotification({
                        severity: 'success',
                        title: "predictionsCalculated",
                    })
                }
            }
        })
    }

    private destroy$ = new Subject<void>();
    ngOnDestroy(): void {
        // cancella le subscription attive
        this.destroy$.next();
        this.destroy$.complete();
    }
    //^ ////////////////////////////////////////////////////////////////////
    //^ FUNZIONI, EVENTI... ------------------------------------------------
    //^ ////////////////////////////////////////////////////////////////////

    //? GESTIONE NOTIFICHE -------------------------------------------------
    showNotification(config: Notification) {
        config = { ...this.configDefaults, ...config }
        let id = config.id
        if (!id || id.trim() === '') {
            id = uuid()
            config.id = id
        } else {
            if (this.notificationsHistory.find(n => n.id === id)) {
                console.warn('Notification with id' + id + 'already exists. Generating new')
                id = uuid()
                config.id = id
            }
        }

        let timestamp = config.timestamp
        if (!timestamp) {
            config.timestamp = new Date()
        }

        if (!config.addToHistory || !this.notificationsPanelVisible) {

            let parsedDetails = this.getNotificationDetails(config, true)
            let parsedDetailsString: string | undefined = parsedDetails.map(n => {
                if (config.translateDetailsParams ?? true) {
                    Object.keys(n.params ?? {}).forEach(k => {
                        n.params[k] = this.transloco.translate(n.params[k])
                    })
                }
                return this.transloco.translate(n.content, n.params)
            }).join('\n')

            let title = ""
            if (config.previewTitle) {
                title = this.transloco.translate(config.previewTitle, config.previewTitleParams)
            } else {
                title = this.transloco.translate(config.title, config.titleParams)
            }

            if (parsedDetailsString == title) {
                parsedDetailsString = undefined
                config.detail = undefined
                config.detailParams = undefined
            }

            let previewSeverity = config.severity
            if (config.severity === "request") {
                previewSeverity = "info"
            }

            this.messages.add({
                id: id,
                severity: previewSeverity,
                summary: title,
                detail: parsedDetailsString,
                life: config.life,
                closable: config.closable,
                sticky: config.sticky,
                icon: config.icon,
            })
        }

        if (config.addToHistory) {
            const priority = config.priority || 0;
            const insertIndex = this.notificationsHistory.findIndex(n =>
                (n.priority || 0) <= priority
            );

            if (insertIndex === -1) {
                this.notificationsHistory.push(config);
            } else {
                this.notificationsHistory.splice(insertIndex, 0, config);
            }
        }
        return id
    }

    showBackendNotification(notificationData: any) {
        if ("errorElements" in notificationData) {
            let msg = ""

            Object.keys(notificationData.errorElements ?? {}).forEach((key) => {
                let value = notificationData.errorElements[key]
                let params = value[1] ?? {}
                Object.keys(params).forEach(k => {
                    params[k] = this.transloco.translate(params[k])
                })
                msg += (this.transloco.translate(value[0], params)) + "\n"
            })

            this.showNotification({
                severity: notificationData.severity ?? 'error',
                title: notificationData.title ?? 'errors.generic',
                titleParams: notificationData.titleParams ?? {},
                detail: "%t%" + msg
            })
        } else {
            if (notificationData.detailParams && "dayIndex" in notificationData.detailParams) {
                let index = notificationData.detailParams.dayIndex
                let postfix = ""
                if ((notificationData.detailParams.dayLength ?? "") == "short") {
                    postfix = "Short"
                }
                notificationData.detailParams.day = "%t%" + this.transloco.translate("primeng.dayNames" + postfix)[index]
                delete notificationData.detailParams.dayIndex
                delete notificationData.detailParams.dayLength
            }
            this.showNotification(notificationData)
        }
    }

    clearNotification(id: string) {
        this.messages.clear(id)
        this.notificationsHistory = this.notificationsHistory.filter(n => n.id !== id)
    }

    clearAllNotifications() {
        this.messages.clear()
        this.notificationsHistory = this.notificationsHistory.filter(n => !(n.deletable ?? true))
        this.clearSelection()
        setTimeout(() => {
            if (this.notificationsHistory.length === 0) {
                this.notificationsPanelVisible = false
            }
        }, 750);
    }

    clearSelectedNotifications() {
        this.notificationsHistory = this.notificationsHistory.filter(n => !this.isNotificationSelected(n) || !(n.deletable ?? true))
        this.clearSelection()
    }

    clearSelection() {
        this.selectedNotifications = []
    }

    clearPopups() {
        this.messages.clear()
    }


    updateNotification(id: string, config: Partial<Notification>) {
        this.selectedNotifications = this.selectedNotifications.filter(n => n.id != id)
        const index = this.notificationsHistory.findIndex(n => n.id === id);
        if (index !== -1) {
            const notification = this.notificationsHistory[index];
            this.notificationsHistory[index] = {
                ...notification,
                ...config
            };
        }
    }
    //. --------------------------------------------------------------------


    //? STATI E PROPRIETA ---------------------------------------------------
    getNotificationIcon(notification: Notification) {
        if (notification.icon) {
            return notification.icon
        } else {
            switch (notification.severity) {
                case 'success':
                    return 'pi pi-check'
                case 'warn':
                    return 'pi pi-exclamation-triangle'
                case 'error':
                    return 'pi pi-exclamation-circle'
                case 'request':
                    return 'pi pi-question'
                default:
                    return 'pi pi-info'
            }
        }
    }

    getNotificationDetails(notification: Notification, isPreview: boolean = false) {
        let detail: any
        let detailParams: any
        if (isPreview) {
            if (notification.previewDetail) {
                detail = notification.previewDetail
                detailParams = notification.previewDetailParams
            } else {
                detail = notification.detail
                detailParams = notification.detailParams
            }
        } else {
            detail = notification.detail
            detailParams = notification.detailParams
        }

        if (detail) {
            if (Array.isArray(detail)) {
                if (Array.isArray(detailParams)) {
                    return detail.map((d, i) => {
                        return { content: d, params: structuredClone(detailParams as any)?.[i] }
                    })
                } else {
                    return detail.map((d, i) => {
                        return { content: d, params: structuredClone(detailParams) }
                    })
                }
            } else {
                return [{ content: detail, params: structuredClone(detailParams) }]
            }
        } else {
            return []
        }
    }

    translateParameters(params: any) {
        Object.keys(params ?? {}).forEach(k => {
            params[k] = this.transloco.translate(params[k])
        })
        return params
    }

    isNotificationSelected(notification: Notification) {
        return this.selectedNotifications.find(n => n.id === notification.id)
    }

    getNotificationsCount() {
        return this.notificationsHistory.length
    }

    getDeletableNotificationsCount() {
        return this.notificationsHistory.filter(n => (n.deletable ?? true)).length
    }

    getNotificationsCountLabel(max: number = 20) {
        let length = this.getNotificationsCount()
        if (length > max) {
            return max + "+"
        }
        return length + ""
    }

    getSelectedNotificationsCount() {
        return this.selectedNotifications.length
    }

    getNotificationsList() {
        return this.notificationsHistory;
    }

    get notificationHistoryGroupedDay() {
        const groupMap = new Map<string, { date: Date, notifications: Notification[] }>();

        for (const notification of this.notificationsHistory) {
            const notificationDate = notification.timestamp ? new Date(notification.timestamp) : new Date();
            const dateString = notificationDate.toDateString();

            if (!groupMap.has(dateString)) {
                groupMap.set(dateString, {
                    date: notificationDate,
                    notifications: []
                });
            }

            groupMap.get(dateString)!.notifications.push(notification);
        }

        return Array.from(groupMap.values())
            .sort((a, b) => b.date.getTime() - a.date.getTime());
    }

    isNotificationSelectable(notification: Notification) {
        return notification.deletable ?? true
    }
    //. --------------------------------------------------------------------


    //? HANDLERS -----------------------------------------------------------
    notificationClicked(notification: Notification, event: MouseEvent) {
        // if (event.ctrlKey) {
        if (!(notification.deletable ?? true)) {
            return
        }
        if (this.isNotificationSelected(notification)) {
            this.selectedNotifications = this.selectedNotifications.filter(n => n.id !== notification.id)
        } else {
            this.selectedNotifications.unshift(notification)
        }
        // }
    }
    //. --------------------------------------------------------------------


    //? ALTRO --------------------------------------------------------------
    toggleNotificationsPanelVisible() {
        this.notificationsPanelVisible = !this.notificationsPanelVisible
        if (this.notificationsPanelVisible) {
            this.clearPopups()
        } else {
            this.clearSelection()
        }
    }
    //. --------------------------------------------------------------------

}